/**
 * Flow design system — the guided, completion-first style for the secretary
 * journey (Typeform one-question-at-a-time + Duolingo progress + TurboTax
 * guidance + WhatsApp familiarity + GOV.UK plain language).
 *
 * Additive on top of the brand tokens; intentionally separate from the
 * enterprise `ui.tsx` primitives (which the officer console still uses).
 * Principles: one primary action per screen, large touch targets, minimal
 * text, icon-supported choices, clear progress.
 */
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Check, ChevronDown, Info, type LucideIcon } from 'lucide-react';
import { cn, ProgressBar, friendlyEstado } from './ui';
import { AppHeader } from './app-header';

// Re-exported so existing imports (`from '../components/flow'`) keep working.
export { ProgressBar, friendlyEstado };

// ---------------------------------------------------------------- shell ----

/**
 * Focused, chromeless shell for the guided intake wizard: production top bar
 * (brand + back + progress) over a calm, centered canvas. No sidebar — the
 * wizard is the one full-screen route left outside the app shell.
 */
export function FlowShell({
  onBack,
  progress,
  width = 'wide',
  showActions = true,
  children,
}: {
  onBack?: () => void;
  /** 0..1 — when set, shows a thin progress bar under the top bar. */
  progress?: number;
  /** 'focused' for one-question screens, 'wide' for grids/lists. */
  width?: 'focused' | 'wide';
  showActions?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <AppHeader onBack={onBack} progress={progress} showActions={showActions} />
      <main className="flex flex-1 flex-col overflow-x-clip px-4 py-8 sm:px-6 sm:py-10">
        <div
          className={cn(
            'mx-auto flex w-full flex-1 flex-col',
            width === 'focused' ? 'max-w-xl' : 'max-w-4xl'
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}

// --------------------------------------------------------------- button ----

type BigButtonVariant = 'primary' | 'soft' | 'ghost';

export const BigButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BigButtonVariant; full?: boolean }
>(({ className, variant = 'primary', full = true, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'inline-flex min-h-[3.5rem] items-center justify-center gap-2 rounded-2xl px-6 text-base font-bold transition-all disabled:pointer-events-none disabled:opacity-40',
      full && 'w-full',
      variant === 'primary' &&
        'bg-primary text-primary-foreground shadow-[0_10px_24px_-10px_rgba(138,46,60,.7)] hover:bg-primary-dark active:scale-[.99]',
      variant === 'soft' && 'bg-primary/10 text-primary-dark hover:bg-primary/15',
      variant === 'ghost' && 'text-ink-soft hover:bg-panel',
      className
    )}
    {...props}
  />
));
BigButton.displayName = 'BigButton';

// ---------------------------------------------------------------- input ----

/** Oversized input for one-question screens. */
export const BigInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'h-16 w-full rounded-2xl border-2 border-line-strong bg-panel px-5 text-xl text-ink shadow-card placeholder:text-ink-faint focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15',
        className
      )}
      {...props}
    />
  )
);
BigInput.displayName = 'BigInput';

// ----------------------------------------------------------- choice card ----

/** Config for a single icon-supported choice (used to drive choice steps). */
export interface FlowChoice {
  value: string;
  label: string;
  sub?: string;
  icon?: LucideIcon;
}

/** Large tappable, icon-supported choice. Used for single/grouped choices. */
export function ChoiceCard({
  icon: Icon,
  label,
  sub,
  selected,
  onClick,
}: {
  icon?: LucideIcon | undefined;
  label: string;
  sub?: string | undefined;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-4 rounded-2xl border-2 bg-panel p-5 text-left transition-all active:scale-[.99]',
        selected
          ? 'border-primary ring-4 ring-primary/15'
          : 'border-line-strong hover:border-ink-faint'
      )}
    >
      {Icon && (
        <span
          className={cn(
            'grid h-14 w-14 shrink-0 place-items-center rounded-2xl transition-colors',
            selected ? 'bg-primary/15 text-primary-dark' : 'bg-canvas text-ink-soft'
          )}
        >
          <Icon className="h-7 w-7" strokeWidth={1.75} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-bold leading-tight text-ink">{label}</span>
        {sub && <span className="mt-0.5 block text-sm text-ink-soft">{sub}</span>}
      </span>
      <span
        className={cn(
          'grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 transition-colors',
          selected ? 'border-primary bg-primary text-white' : 'border-line-strong text-transparent'
        )}
      >
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    </button>
  );
}

// ----------------------------------------------------------- field help ----

/**
 * Inline ⓘ popover with a concise plain-language explanation for a field (used to
 * surface each matrix factor's `explicacion` next to its input). Closes on
 * click-outside or Escape — same behaviour as the console's InfoBubble, kept here
 * so the guided flow doesn't depend on `pages/MatrizRiesgo.tsx`.
 */
export function FieldHelp({
  text,
  label,
  align = 'left',
}: {
  text: string;
  label: string;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          'shrink-0 transition-colors',
          open ? 'text-primary' : 'text-ink-faint hover:text-primary'
        )}
      >
        <Info className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && (
        <span
          className={cn(
            'absolute top-full z-30 mt-1 block w-72 rounded-xl border border-line bg-panel p-3 text-xs leading-relaxed text-ink-soft shadow-lg',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {text}
        </span>
      )}
    </span>
  );
}

// --------------------------------------------------------------- toggle ----

/** Two-state Sí/No segmented control — large touch targets, one tap to answer. */
export function Toggle({
  value,
  onChange,
  yesLabel = 'Sí',
  noLabel = 'No',
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  yesLabel?: string;
  noLabel?: string;
}) {
  const opts: { v: boolean; l: string }[] = [
    { v: true, l: yesLabel },
    { v: false, l: noLabel },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-xl border-2 border-line-strong">
      {opts.map(({ v, l }) => (
        <button
          key={l}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'min-w-[4.5rem] px-4 py-2.5 text-sm font-bold transition-colors',
            value === v
              ? 'bg-primary text-primary-foreground'
              : 'bg-panel text-ink-soft hover:bg-canvas'
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

// --------------------------------------------------------------- select ----

export interface FlowOption {
  value: string;
  label: string;
}

/**
 * Styled native `<select>` for the guided flow. Native on purpose: the OS picker is
 * CSP-safe, keyboard type-ahead works over long lists (e.g. the country catalog),
 * and the 16px font avoids the iOS focus-zoom. An empty `placeholder` renders a
 * disabled-looking first option so "elige…" is the resting state.
 */
export const FlowSelect = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: FlowOption[]; placeholder?: string }
>(({ className, options, placeholder, ...props }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      className={cn(
        'h-14 w-full appearance-none rounded-2xl border-2 border-line-strong bg-panel pl-5 pr-11 text-base text-ink shadow-card focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15',
        className
      )}
      {...props}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown
      className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-faint"
      strokeWidth={2}
    />
  </div>
));
FlowSelect.displayName = 'FlowSelect';

// ------------------------------------------------------- question screen ----

/** Big plain-language question + content slot + a single pinned action. */
export function QuestionScreen({
  step,
  question,
  helper,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  secondary,
}: {
  /** e.g. "Paso 2 de 7" */
  step?: string;
  question: string;
  helper?: string;
  children?: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  secondary?: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1">
        {step && (
          <div className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-faint">
            {step}
          </div>
        )}
        <h1 className="font-serif text-3xl font-medium leading-tight text-ink sm:text-4xl">
          {question}
        </h1>
        {helper && <p className="mt-3 text-base text-ink-soft">{helper}</p>}
        {children && <div className="mt-8">{children}</div>}
      </div>

      <div className="sticky bottom-0 mt-8 space-y-3 bg-gradient-to-t from-canvas via-canvas pt-4">
        <BigButton onClick={onPrimary} disabled={primaryDisabled}>
          {primaryLabel}
        </BigButton>
        {secondary}
      </div>
    </div>
  );
}

// --------------------------------------------------------- celebration ----

export function Celebrate({
  title,
  message,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
}: {
  title: string;
  message?: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
      <span className="grid h-24 w-24 animate-pop place-items-center rounded-full bg-primary/12">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-primary text-white shadow-[0_12px_28px_-10px_rgba(138,46,60,.8)]">
          <Check className="h-9 w-9" strokeWidth={3} />
        </span>
      </span>
      <h1 className="mt-6 font-serif text-3xl font-medium text-ink sm:text-4xl">{title}</h1>
      {message && <p className="mt-3 max-w-sm text-base text-ink-soft">{message}</p>}
      <div className="mt-8 w-full max-w-sm space-y-3">
        <BigButton onClick={onPrimary}>{primaryLabel}</BigButton>
        {secondaryLabel && onSecondary && (
          <BigButton variant="ghost" onClick={onSecondary}>
            {secondaryLabel}
          </BigButton>
        )}
      </div>
    </div>
  );
}
